// frontend/src/services/api.js
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const AUTH_TOKEN_KEY = 'theleaf_auth_token';

class ApiService {
  async request(endpoint, options = {}, _retried = false) {
    const url = `${API_BASE_URL}${endpoint}`;

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          // Harmless to our own backend; tells ngrok's free-tier interstitial to
          // pass API requests straight through instead of answering with HTML.
          'ngrok-skip-browser-warning': 'true',
          ...(localStorage.getItem(AUTH_TOKEN_KEY)
            ? { Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY)}` }
            : {}),
          ...options.headers,
        },
      });

      if (!response.ok) {
        const err = new Error(`API Error: ${response.status}`);
        err.status = response.status;
        throw err;
      }

      return await response.json();
    } catch (error) {
      // Network-level failures (tunnel reconnect, interface change) abort
      // fetches with a TypeError and no status. Retry ONCE after a short
      // pause — without this, a single blip leaves cards empty until the
      // next bar triggers a refetch.
      const isNetworkError = error instanceof TypeError && error.status === undefined;
      if (isNetworkError && !_retried) {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        return this.request(endpoint, options, true);
      }
      console.error('API Request failed:', error);
      throw error;
    }
  }

  // Market Data
  async getMarketData(symbol, interval = '1d', period = '1mo') {
    return this.request(`/market/${symbol}?interval=${interval}&period=${period}`);
  }

  // Enhanced Market Data with extended historical data
  async getExtendedMarketData(symbol, interval = '1d', maxDataPoints = 5000) {
    return this.request(`/market/${symbol}/extended?interval=${interval}&max_points=${maxDataPoints}`);
  }

  // Progressive data loading - get data from a specific time range
  async getMarketDataRange(symbol, interval = '1d', startTime, endTime) {
    const start = typeof startTime === 'number' ? startTime : new Date(startTime).getTime();
    const end = typeof endTime === 'number' ? endTime : new Date(endTime).getTime();
    return this.request(`/market/${symbol}/range?interval=${interval}&start=${start}&end=${end}`);
  }

  // Indicators
  async getIndicators(symbol, interval = '1d') {
    return this.request(`/indicators/${symbol}?interval=${interval}`);
  }

  // Signals
  async getSignals(symbol, interval = '1d', lookback = 20) {
    return this.request(`/signals/${symbol}?interval=${interval}&lookback=${lookback}`);
  }

  // Ticker Info
  async getTickerInfo(symbol) {
    return this.request(`/ticker/info/${symbol}`);
  }

  // Search Symbols
  async searchSymbols(query) {
    return this.request(`/symbols/search?q=${query}`);
  }

  // Backtest
  async runBacktest(data) {
    return this.request('/backtest', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Market Metrics
  async getMarketMetrics(payload) {
    return this.request('/metrics', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // Regime Intelligence (latent-state regime model)
  async getRegimeIntel(payload) {
    return this.request('/regime-intel', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // Strategy Lab (multi-strategy backtest + ensemble signal)
  async getStrategyLab(payload) {
    return this.request('/strategy-lab', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // Trading Analysis
  async getTradingAnalysis(payload) {
    return this.request('/analysis', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // Pattern prediction
  async getPrediction(payload) {
    return this.request('/predict', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async getPairTrading(symbolA, symbolB) {
    return this.request(`/pair-trading?symbolA=${encodeURIComponent(symbolA)}&symbolB=${encodeURIComponent(symbolB)}`);
  }

  async loginWithGoogle(credential) {
    return this.request('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ credential }),
    });
  }

  async getCurrentUser() {
    return this.request('/auth/me');
  }
}

// Create singleton instance
const apiService = new ApiService();

// Export individual functions for easier use
export const fetchMarketData = (symbol, interval, period) => 
  apiService.getMarketData(symbol, interval, period);

export const fetchExtendedMarketData = (symbol, interval, maxDataPoints) => 
  apiService.getExtendedMarketData(symbol, interval, maxDataPoints);

export const fetchMarketDataRange = (symbol, interval, startTime, endTime) => 
  apiService.getMarketDataRange(symbol, interval, startTime, endTime);

export const fetchIndicators = (symbol, interval) => 
  apiService.getIndicators(symbol, interval);

export const fetchSignals = (symbol, interval, lookback) => 
  apiService.getSignals(symbol, interval, lookback);

export const fetchTickerInfo = (symbol) => 
  apiService.getTickerInfo(symbol);

export const searchSymbols = (query) => 
  apiService.searchSymbols(query);

export const runBacktest = (data) => 
  apiService.runBacktest(data);

export const fetchMarketMetrics = (payload) =>
  apiService.getMarketMetrics(payload);

export const fetchRegimeIntel = (payload) =>
  apiService.getRegimeIntel(payload);

export const fetchStrategyLab = (payload) =>
  apiService.getStrategyLab(payload);

export const fetchTradingAnalysis = (payload) =>
  apiService.getTradingAnalysis(payload);

export const fetchPrediction = (payload) =>
  apiService.getPrediction(payload);

export const fetchPairTrading = (symbolA, symbolB) =>
  apiService.getPairTrading(symbolA, symbolB);

export const loginWithGoogle = (credential) =>
  apiService.loginWithGoogle(credential);

export const fetchCurrentUser = () =>
  apiService.getCurrentUser();

export const fetchAdminOverview = () =>
  apiService.request('/admin/overview');

export const fetchWatchlist = () =>
  apiService.request('/watchlist');

export const addWatch = (symbol, minScore = 65) =>
  apiService.request('/watchlist', {
    method: 'POST',
    body: JSON.stringify({ symbol, minScore }),
  });

export const removeWatch = (symbol) =>
  apiService.request(`/watchlist/${encodeURIComponent(symbol)}`, { method: 'DELETE' });

export const fetchAlertSettings = () =>
  apiService.request('/alert-settings');


export default apiService;
